"use client"

import React, { useEffect, useRef, useState } from "react"

declare global {
  interface Window {
    Tally?: { loadEmbeds?: () => void }
  }
}

const TALLY_EMBED_SCRIPT_URL = "https://tally.so/widgets/embed.js"
const MIN_EMBED_HEIGHT = 50
const INITIAL_LOAD_DELAY_MS = 800
const BLOCKED_CHECK_DELAYS_MS = [4000, 6000, 9000]

/**
 * Extracts the first Tally form embed URL from HTML (e.g. from iframe src or data attributes).
 * Ad-blockers often block Tally iframes/scripts; we use this to show a fallback "open in new tab" link.
 */
function extractTallyFormUrl(html: string): string | null {
  if (!html || typeof html !== "string") return null
  const match = html.match(
    /https:\/\/tally\.so\/embed\/[a-zA-Z0-9_-]+/i
  )
  return match ? match[0] : null
}

/** Returns true if the HTML contains Tally embed (iframe or script). */
function isTallyEmbed(html: string): boolean {
  return (
    typeof html === "string" &&
    (html.includes("tally.so/embed") || html.includes("tally.so/widgets/embed"))
  )
}

/**
 * Ensures the Tally embed script is loaded. Scripts in innerHTML do not execute, so we must
 * load the script explicitly. Resolves when Tally.loadEmbeds is available (script loaded or already present).
 */
function loadTallyScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve()
  if (window.Tally?.loadEmbeds) return Promise.resolve()
  const existing = document.querySelector(`script[src="${TALLY_EMBED_SCRIPT_URL}"]`)
  if (existing) {
    return new Promise((resolve) => {
      if (window.Tally?.loadEmbeds) {
        resolve()
        return
      }
      existing.addEventListener("load", () => resolve())
      window.setTimeout(resolve, 3000)
    })
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = TALLY_EMBED_SCRIPT_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("Tally script failed to load"))
    document.head.appendChild(script)
  })
}

function isEmbedLoaded(container: HTMLDivElement | null): boolean {
  if (!container) return false
  const iframe = container.querySelector<HTMLIFrameElement>(
    'iframe[src*="tally.so"], iframe[data-tally-embed]'
  )
  if (!iframe) return false
  return iframe.offsetHeight >= MIN_EMBED_HEIGHT && iframe.offsetWidth >= MIN_EMBED_HEIGHT
}

/**
 * Detects if an embed (e.g. Tally iframe) likely failed to load (e.g. due to ad-blocker).
 * Calls Tally.loadEmbeds() after mount so dynamically injected embeds initialize reliably.
 * Only shows fallback after multiple checks with longer delays to avoid false positives when the script loads slowly.
 */
function useEmbedLoadState(containerRef: React.RefObject<HTMLDivElement | null>, html: string) {
  const [likelyBlocked, setLikelyBlocked] = useState(false)
  const tallyUrl = extractTallyFormUrl(html)
  const hasTally = Boolean(tallyUrl)

  // 1) Tell Tally to (re-)load embeds once our container is in the DOM. Fixes race where
  //    the embed script ran before our div existed, so the form never initializes.
  useEffect(() => {
    if (!hasTally) return

    const runLoadEmbeds = () => {
      try {
        window.Tally?.loadEmbeds?.()
      } catch {
        // Tally script may not be loaded yet
      }
    }

    const t1 = window.setTimeout(runLoadEmbeds, INITIAL_LOAD_DELAY_MS)
    const t2 = window.setTimeout(runLoadEmbeds, 2500)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
    }
  }, [hasTally, html])

  // 2) Only show "blocked" fallback after several checks at 4s, 6s, 9s. If the iframe ever
  //    appears (e.g. slow script load), we never or no longer show fallback.
  useEffect(() => {
    if (!hasTally || !containerRef.current) return

    const timeouts: number[] = []

    BLOCKED_CHECK_DELAYS_MS.forEach((delayMs) => {
      const id = window.setTimeout(() => {
        const container = containerRef.current
        if (isEmbedLoaded(container)) {
          setLikelyBlocked(false)
          return
        }
        // Only set blocked on the last check so we don't flash fallback when load is just slow
        const isLastCheck = delayMs === BLOCKED_CHECK_DELAYS_MS[BLOCKED_CHECK_DELAYS_MS.length - 1]
        if (isLastCheck) {
          setLikelyBlocked(true)
        }
      }, delayMs)
      timeouts.push(id)
    })

    return () => timeouts.forEach((id) => window.clearTimeout(id))
  }, [hasTally, html, containerRef])

  // 3) If the iframe appears later (e.g. after we already showed fallback), hide fallback.
  useEffect(() => {
    if (!hasTally || !likelyBlocked) return

    const interval = window.setInterval(() => {
      if (isEmbedLoaded(containerRef.current)) {
        setLikelyBlocked(false)
      }
    }, 1500)
    return () => clearInterval(interval)
  }, [hasTally, likelyBlocked, containerRef])

  return { likelyBlocked, tallyUrl }
}

export interface EmbedWithFallbackProps {
  /** Raw HTML for the embed (e.g. Tally form script + iframe). */
  html: string
  /** Optional class name for the wrapper. */
  className?: string
  /** Optional test id for the wrapper. */
  "data-testid"?: string
}

/**
 * Renders arbitrary embed HTML (e.g. Tally forms) and shows a fallback message + link
 * when the embed is likely blocked (e.g. by ad-blockers). Users can open the form in a new tab.
 *
 * HTML is injected only on the client (in useEffect) to avoid hydration mismatch: server and
 * client both render an empty container, then we set innerHTML after mount. This fixes Tally
 * and other embeds that would otherwise differ between server and client (e.g. attribute order).
 */
export function EmbedWithFallback({
  html,
  className,
  "data-testid": dataTestId,
}: EmbedWithFallbackProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const htmlRef = useRef(html)
  htmlRef.current = html

  const { likelyBlocked, tallyUrl } = useEmbedLoadState(containerRef, html)

  // Inject HTML only after mount so server and client render the same empty div (no hydration mismatch).
  // Scripts inside innerHTML never execute, so we must load the Tally script explicitly first.
  useEffect(() => {
    const el = containerRef.current
    const rawHtml = htmlRef.current
    if (!el || !rawHtml) return

    const injectAndInit = () => {
      el.innerHTML = rawHtml
      try {
        window.Tally?.loadEmbeds?.()
      } catch {
        // no-op
      }
    }

    if (isTallyEmbed(rawHtml)) {
      loadTallyScript()
        .then(injectAndInit)
        .catch(() => injectAndInit())
      const t = window.setTimeout(injectAndInit, 2500)
      return () => window.clearTimeout(t)
    }

    injectAndInit()
  }, [html])

  return (
    <div className={className} data-testid={dataTestId}>
      <div ref={containerRef} />
      {likelyBlocked && (
        <div
          className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="embed-fallback-message"
          aria-live="polite"
          role="status"
        >
          <p className="font-medium">Form not visible?</p>
          <p className="mt-1 text-amber-800">
            If you use an ad-blocker or privacy extension, the sign-up form above may be hidden.
            {tallyUrl ? (
              <>
                {" "}
                <a
                  href={tallyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium hover:no-underline"
                  data-testid="embed-fallback-link"
                >
                  Open the form in a new tab
                </a>
                .
              </>
            ) : (
              " Try disabling it for this site or open the page in a private window."
            )}
          </p>
        </div>
      )}
    </div>
  )
}
